/**
 * `/feed` — the global feed (FEED_SPEC, endpoint #51).
 *
 * The manual half of viral monitoring: every watched profile's posts on one screen, scoped by a
 * persistent left sidebar instead of a popover. The card, the sort/range controls, the cursor and
 * the `?post=` preview are the view screen's, unchanged — what is new is the scope and the sidebar.
 *
 * Three things worth knowing about this file:
 *
 *  - **The ui-state gate.** `feed:main` loads before the first #51 request (§12.4), so a persisted
 *    scope is never briefly ignored and page one is never fetched twice.
 *  - **The list never moves on its own.** A scrape finishing arms a quiet "New posts" pill; only a
 *    click on it invalidates the query. Auto-invalidating would shift the list under the reader,
 *    which is the one thing a feed must not do.
 *  - **The 552px track is centred in the viewport**, with the 288px sidebar hung off its left in a
 *    three-track grid — reading happens in the track, so that is what centres. Below 1100px the
 *    sidebar becomes a drawer; it is unmounted rather than hidden, since a hidden drawer still
 *    answers Escape.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { FeedCardDTO, FeedFiltersState, ViewRangeState } from '@lw/shared';
import { pageTitle } from '../../app/appTitle';
import { HOME_PATH } from '../../app/routes';
import { CloseIcon, EmptyState, ErrorState, Spinner, cx } from '../../components';
import { useDismissable, useInView, useDocumentTitle, useMediaQuery } from '../../lib/hooks';
import { useSseEvent } from '../../sse';
import { useQueryClient } from '@tanstack/react-query';
import { QK } from '../../api/queryKeys';
import { useGroupsQuery, useProfilesQuery } from '../home/useHomeData';
import {
  useAppTimezone,
  useMediaEnabled,
  useSettingsQuery,
  useTimezoneBootstrap,
} from '../settings/useSettings';
import { NotionSaveProvider } from '../notion/useNotionSave';
import { SettingsModal } from '../settings/SettingsModal';
import { FeedCard } from '../view/feed/FeedCard';
import { PostPreviewProvider, usePostPreview } from '../view/PreviewContext';
import { anchorNow, resolveRange, type ViewSort } from '../view/viewState';
import { FeedHeader } from './FeedHeader';
import { FeedSidebar } from './FeedSidebar';
import { DEFAULT_OPEN_SECTIONS, pruneScope, toFeedParams, totalFilterCount, type FeedSectionId } from './feedState';
import { useFeedQuery } from './useFeedQuery';
import { useFeedUiState } from './useFeedUiState';
import './feed.css';

/** Below this the sidebar unmounts and the header grows a Filter trigger (FEED_SPEC § Layout). */
const SIDEBAR_QUERY = '(min-width: 1100px)';

export function FeedPage() {
  useTimezoneBootstrap();
  useDocumentTitle(pageTitle('Feed'));

  const timezone = useAppTimezone();
  const mediaEnabled = useMediaEnabled();
  const countedPostTypes = useSettingsQuery().data?.countedPostTypes ?? [];
  const [settingsOpen, setSettingsOpen] = useState(false);

  const uiState = useFeedUiState();
  const wide = useMediaQuery(SIDEBAR_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Session-only, and owned here rather than by the sidebar so the drawer and the island share it.
  const [openSections, setOpenSections] = useState<ReadonlySet<FeedSectionId>>(
    () => new Set(DEFAULT_OPEN_SECTIONS),
  );

  const profilesQuery = useProfilesQuery();
  const groupsQuery = useGroupsQuery();
  const groups = groupsQuery.data?.groups ?? [];
  // Archived profiles are out of the feed's scope entirely, so they are not offerable either.
  const activeProfiles = (profilesQuery.data?.profiles ?? []).filter(
    (profile) => profile.archivedAt === undefined || profile.archivedAt === null,
  );

  /* ----------------------------------------------------------- range/params */

  const rangeKey = `${uiState.state.range.preset}|${uiState.state.range.from ?? ''}|${uiState.state.range.to ?? ''}`;
  const now = useMemo(() => anchorNow(), [rangeKey]);
  const resolvedRange = useMemo(() => resolveRange(uiState.state.range, now), [rangeKey, now]);

  const filtersKey = JSON.stringify(uiState.state.filters);
  const sortKey = `${uiState.state.sort.key}|${uiState.state.sort.dir}`;
  const params = useMemo(
    () => toFeedParams(uiState.state, resolvedRange),
    [filtersKey, sortKey, resolvedRange],
  );

  const feed = useFeedQuery(params, uiState.ready);
  const cards = feed.data?.pages.flatMap((page) => page.cards) ?? [];
  // `meta` rides the cursor-less page only, which is always the first one in the cache.
  const meta = feed.data?.pages[0]?.meta;

  /* ------------------------------------------------------------- pruning */

  // A persisted selection can outlive the group it names, or the profile it names can be archived.
  // The server ignores dangling ids regardless; this keeps the *chips* honest. Runs once per load,
  // and only when something actually dangles, so it cannot loop against its own write.
  const prunedRef = useRef(false);
  useEffect(() => {
    if (prunedRef.current || !uiState.ready) return;
    if (!groupsQuery.isSuccess || !profilesQuery.isSuccess) return;
    prunedRef.current = true;
    const next = pruneScope(
      uiState.state.filters,
      new Set(groups.map((group) => group.id)),
      new Set(activeProfiles.map((profile) => profile.id)),
    );
    if (next !== null) uiState.update({ filters: next });
  }, [uiState, groupsQuery.isSuccess, profilesQuery.isSuccess, groups, activeProfiles]);

  /* ------------------------------------------------------- "New posts" pill */

  const [hasNewPosts, setHasNewPosts] = useState(false);
  useSseEvent('run.updated', (event) => {
    // Only a finished run can have added posts; `queued`/`running` say nothing yet.
    if (event.status === 'queued' || event.status === 'running') return;
    setHasNewPosts(true);
  });

  const queryClient = useQueryClient();
  const refreshFeed = () => {
    setHasNewPosts(false);
    void queryClient.invalidateQueries({ queryKey: QK.feed });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* ---------------------------------------------------------------- render */

  const setFilters = (filters: FeedFiltersState) => uiState.update({ filters });
  const setRange = (range: ViewRangeState) => uiState.update({ range });
  const setSort = (sort: ViewSort) => uiState.update({ sort });
  const toggleSection = (section: FeedSectionId) =>
    setOpenSections((previous) => {
      const next = new Set(previous);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });

  const filterCount = totalFilterCount(uiState.state.filters);
  const noProfiles = profilesQuery.isSuccess && activeProfiles.length === 0;

  const sidebar = (
    <FeedSidebar
      filters={uiState.state.filters}
      onChange={setFilters}
      groups={groups}
      profiles={activeProfiles}
      mediaEnabled={mediaEnabled}
      countedPostTypes={countedPostTypes}
      openSections={openSections}
      onToggleSection={toggleSection}
    />
  );

  return (
    <NotionSaveProvider onOpenSettings={() => setSettingsOpen(true)}>
      <PostPreviewProvider mediaEnabled={mediaEnabled} timezone={timezone}>
        <div className="feed-screen">
          <FeedHeader
            summaryDisplay={meta?.summaryDisplay}
            sort={uiState.state.sort}
            onSortChange={setSort}
            range={uiState.state.range}
            resolvedRange={resolvedRange}
            onRangeChange={setRange}
            timezone={timezone}
            now={now}
            onOpenFilters={wide ? null : () => setDrawerOpen(true)}
            filterCount={filterCount}
          />

          <div className="feed-screen__body">
            <div className={cx('feed-layout', !wide && 'feed-layout--narrow')}>
              {wide ? <aside className="feed-layout__side">{sidebar}</aside> : null}

              <main className="feed-layout__main">
                {uiState.error !== null ? (
                  <ErrorState
                    title="Couldn't restore your saved filters"
                    description="The feed is showing the default range and filters. Retrying re-reads what was stored."
                    onRetry={uiState.retry}
                  />
                ) : null}

                {hasNewPosts ? (
                  <button type="button" className="feed-new" onClick={refreshFeed}>
                    New posts — refresh
                  </button>
                ) : null}

                <FeedList
                  cards={cards}
                  mediaEnabled={mediaEnabled}
                  isPending={!uiState.ready || feed.isPending}
                  error={feed.isError ? feed.error : null}
                  onRetry={() => void feed.refetch()}
                  retrying={feed.isFetching}
                  hasNextPage={feed.hasNextPage}
                  isFetchingNextPage={feed.isFetchingNextPage}
                  fetchNextPage={() => void feed.fetchNextPage()}
                  empty={
                    noProfiles ? (
                      <EmptyState
                        title="No profiles yet"
                        description="The feed shows posts from the profiles you watch. Add a few to get started."
                        action={
                          <Link className="btn-primary" to={HOME_PATH}>
                            Add profiles
                          </Link>
                        }
                      />
                    ) : filterCount > 0 ? (
                      <EmptyState
                        title="No posts match these filters"
                        description="Clear the filters, or widen the date range."
                        action={
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={() => setFilters({})}
                          >
                            Clear filters
                          </button>
                        }
                      />
                    ) : (
                      <EmptyState
                        title="No posts yet — run a scrape"
                        description="Nothing has been collected in this date range yet. Scrape a profile, or widen the range."
                        action={
                          <Link className="btn-primary" to={HOME_PATH}>
                            Go to profiles
                          </Link>
                        }
                      />
                    )
                  }
                />
              </main>
            </div>
          </div>

          {!wide && drawerOpen ? (
            <FeedDrawer onClose={() => setDrawerOpen(false)}>{sidebar}</FeedDrawer>
          ) : null}

          <SettingsModal
            open={settingsOpen}
            initialTab="notion"
            onClose={() => setSettingsOpen(false)}
          />
        </div>
      </PostPreviewProvider>
    </NotionSaveProvider>
  );
}

type FeedListProps = {
  cards: FeedCardDTO[];
  mediaEnabled: boolean;
  isPending: boolean;
  error: Error | null;
  onRetry: () => void;
  retrying: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  empty: ReactNode;
};

/** The 552px card track. Split out only so the preview context is available above it. */
function FeedList({
  cards,
  mediaEnabled,
  isPending,
  error,
  onRetry,
  retrying,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  empty,
}: FeedListProps) {
  const { openPreview } = usePostPreview();
  const ids = cards.map((card) => card.id);

  const sentinelRef = useInView<HTMLDivElement>(hasNextPage && !isFetchingNextPage, fetchNextPage, {
    rootMargin: '400px',
  });

  return (
    <div className="feed">
      {isPending ? <Spinner size="lg" label="Loading feed…" /> : null}

      {error !== null ? (
        <ErrorState
          title="Couldn't load the feed"
          error={error}
          onRetry={onRetry}
          retrying={retrying}
        />
      ) : null}

      {!isPending && error === null && cards.length === 0 ? empty : null}

      <div className="feed__list">
        {cards.map((card) => (
          <FeedCard
            key={card.id}
            card={card}
            mediaEnabled={mediaEnabled}
            onOpen={(postId) => openPreview(postId, ids)}
          />
        ))}
      </div>

      <div ref={sentinelRef} className="feed__sentinel">
        {isFetchingNextPage ? <Spinner label="Loading more…" /> : null}
        {!hasNextPage && cards.length > 0 ? <span className="muted">End of the feed.</span> : null}
      </div>
    </div>
  );
}

/**
 * The sidebar as a left slide-in with a scrim, under the breakpoint. Escape and an outside press
 * close it through `useDismissable`, the same mechanics every popover uses.
 */
function FeedDrawer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useDismissable<HTMLDivElement>(true, onClose);

  return (
    <div className="feed-drawer" role="dialog" aria-label="Filters" aria-modal="true">
      <div className="feed-drawer__scrim" />
      <div className="feed-drawer__panel" ref={ref}>
        <div className="feed-drawer__head">
          <span className="feed-drawer__title">Filters</span>
          <button type="button" className="icon-btn" aria-label="Close filters" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default FeedPage;
